// Wallet.jsx
import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CachedIcon from "@mui/icons-material/Cached";
import UserLayout from "../../layouts/UserLayout";
import { Button, Input, message } from "antd";
import Loader from "../../components/Loader";
import API from "../../utils/api";
import Transacrions from "./Transacrions";

const MoneyCounter = ({ amount }) => {
  const [displayAmount, setDisplayAmount] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = amount / (duration / 16);

    const animateCount = () => {
      start = Math.min(start + increment, amount);
      setDisplayAmount(start);
      if (start < amount) requestAnimationFrame(animateCount);
    };

    animateCount();
  }, [amount]);

  return (
    <p className="text-3xl font-bold text-white">
      ₹ {displayAmount.toLocaleString("en-IN")}.00
    </p>
  );
};

const Wallet = () => {
  const [amount, setAmount] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const transactionRef = useRef(null);

  const predefinedAmounts = [2000, 5000, 10000];

  const formatDate = (createdAt) =>
    new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(createdAt));

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleInstamojoPayment = async () => {
    if (!amount || Number(amount) <= 0)
      return message.error("Please enter a valid amount!");

    try {
      setLoading(true);
      const { data } = await API.post("/wallet/add-balance", {
        amount: Number(amount),
      });

      message.success("Balance added successfully!");
      if (data.paymentURL) window.open(data.paymentURL, "_blank");

      closeModal();
      setAmount("");
      getUserData();
    } catch (error) {
      console.error("Add balance error:", error);
      message.error("Failed to add balance.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhonePePayment = async () => {
    if (!amount || Number(amount) <= 0) {
      return message.error("Please enter a valid amount!");
    }

    try {
      setLoading(true);

      const { data } = await API.post("/wallet/add-balance-phonepe", {
        amount: Number(amount),
      });

      console.log("PhonePe response:", data);

      if (data.paymentURL) {
        // Close modal and clear amount before redirecting
        setAmount("");
        closeModal();

        // Redirect to PhonePe checkout
        window.location.href = data.paymentURL;
        return;
      }

      message.error("PhonePe payment URL not received.");
    } catch (error) {
      console.error("PhonePe error:", error);
      message.error(
        error?.response?.data?.message || "Failed to initiate PhonePe payment.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePayGlocalPayment = async () => {
    if (!amount || Number(amount) <= 0) {
      return message.error("Please enter a valid amount!");
    }

    try {
      setLoading(true);

      const { data } = await API.post("/wallet/add-balance-payglocal", {
        amount: Number(amount),
      });

      console.log(data);

      if (data.paymentURL) {
        localStorage.setItem("gid", data.raw.gid);
        window.location.href = data.paymentURL;
        return;
      }

      message.error("Payment URL not received.");
    } catch (error) {
      console.error("PayGlocal error:", error);
      message.error("Failed to initiate payment.");
    } finally {
      setLoading(false);
      setAmount("");
      closeModal();
    }
  };

  const getUserData = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/user/user-details");
      setUserData(data.user);
    } catch (error) {
      console.error("Fetch user error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  const scrollToTransactions = () => {
    if (transactionRef.current) {
      transactionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  const handleDownload = async () => {
    try {
      const res = await API.get("/user/download-transactions", {
        responseType: "blob", // important for file download
      });

      // Create blob link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Transaction_History.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      message.error("Failed to download transaction history");
    }
  };

  if (loading) return <Loader />;

  return (
    <UserLayout>
      <div className="max-w-4xl mx-auto p-6 pt-0">
        {/* Balance Section */}
        <div className="bg-purple-600 text-white p-4 rounded-lg flex justify-between items-center mb-6 mt-10">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">Wallet Balance</h3>
              <CachedIcon className="cursor-pointer" onClick={getUserData} />
            </div>
            {userData?.amount !== undefined && (
              <MoneyCounter amount={userData.amount} />
            )}
            <button
              onClick={scrollToTransactions}
              className="text-sm underline"
            >
              View Transactions
            </button>
          </div>
          <button
            onClick={openModal}
            className="bg-purple-200 text-black py-2 px-4 rounded-lg hover:bg-purple-300 transition"
          >
            ➕ Add Balance
          </button>
        </div>

        {/* Add Balance Quick Buttons */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-6 text-black">
          <h3 className="text-lg font-bold mb-4">Quick Add:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {predefinedAmounts.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(amt)}
                className="border-2 border-purple-600 py-2 px-4 rounded-lg hover:bg-purple-100 transition"
              >
                ₹{amt.toLocaleString("en-IN")}.00
              </button>
            ))}
          </div>
          <label className="block mb-2 font-semibold">Custom Amount:</label>
          <Input
            type="number"
            placeholder="Enter amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mb-4"
          />
          <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
            {/* <button
              onClick={handleInstamojoPayment}
              className="bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition"
            >
              Pay with Instamojo
            </button> */}

            {/* <button
              onClick={handlePhonePePayment}
              className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
            >
              Pay with PhonePe
            </button> */}

            <button
              onClick={handlePayGlocalPayment}
              className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition"
            >
              Pay with PayGlocal
            </button>
          </div>
          <div className="mt-4 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              <span className="font-semibold">Note:</span> A 2.36% payment
              gateway processing fee will be added to the payment amount.
              {Number(amount) > 0 && (
                <>
                  {" "}
                  Total payable:{" "}
                  <span className="font-semibold text-gray-700">
                    ₹
                    {(Number(amount) * 1.0236).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Transaction History */}
        <div
          id="transactionHistory"
          ref={transactionRef}
          className="bg-white p-4 rounded-lg shadow-md text-black"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Recent Transactions</h3>
            <Button type="primary" onClick={handleDownload}>
              Download Transaction History
            </Button>
          </div>
          <Transacrions />
          {/* <ul className="space-y-2">
            {userData?.transactions
              ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .map((t, i) => (
                <li
                  key={i}
                  className="flex justify-between items-center border-b py-2"
                >
                  <div>
                    <p className="font-semibold text-sm">
                      {t.description || "No description"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(t.createdAt)}
                    </p>
                  </div>
                  <p
                    className={`font-bold ${
                      t.credit ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    ₹{t.amount.toLocaleString("en-IN")}
                  </p>
                </li>
              ))}
          </ul> */}
        </div>

        {/* Modal */}

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/8 backdrop-blur-sm p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                initial={{
                  opacity: 0,
                  rotateY: -90,
                  x: -100,
                  scale: 0.95,
                }}
                animate={{
                  opacity: 1,
                  rotateY: 0,
                  x: 0,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  rotateY: 90,
                  x: 100,
                  scale: 0.95,
                }}
                transition={{
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  perspective: 1200,
                  transformStyle: "preserve-3d",
                }}
                className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl origin-center"
              >
                <h3 className="text-xl font-bold mb-4">Add Balance</h3>

                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mb-4"
                />

                <div className="space-y-3">
                  {/* <button
                    onClick={handleInstamojoPayment}
                    className="bg-purple-600 text-white py-2 px-4 rounded-lg w-full hover:bg-purple-700 transition-colors"
                  >
                    Pay with Instamojo
                  </button> */}

                  <button
                    onClick={handlePhonePePayment}
                    className="bg-blue-600 text-white py-2 px-4 rounded-lg w-full hover:bg-blue-700 transition-colors"
                  >
                    Pay with PhonePe
                  </button>

                  <button
                    onClick={handlePayGlocalPayment}
                    className="bg-green-600 text-white py-2 px-4 rounded-lg w-full hover:bg-green-700 transition-colors"
                  >
                    Pay with PayGlocal
                  </button>
                </div>

                <button
                  onClick={closeModal}
                  className="mt-3 bg-gray-600 text-white py-2 px-4 rounded-lg w-full hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </UserLayout>
  );
};

export default Wallet;
